import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Set this to the same base your other admin API calls use.
const DEFAULT_API_BASE = 'https://api.konectx.net/api';
const ACCENT = '#1D6EB7'; // KonectX blue

type LaunchConfig = {
  launchDate: string | null;
  progressPercent: number;
  title: string;
  message: string;
  enabled: boolean;
};

type Props = {
  // No raw token is accepted as a prop (identity ADR). Auth is carried by the
  // HttpOnly session cookie on each request.
  apiBase?: string;
};

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function toISO(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function fromNowDays(days: number): string {
  return toLocalInput(new Date(Date.now() + days * 86400000).toISOString());
}

export default function LaunchCountdownAdmin({ apiBase = DEFAULT_API_BASE }: Props) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [launchDate, setLaunchDate] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'saving' | 'saved' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  function applyConfig(data: LaunchConfig) {
    setEnabled(!!data.enabled);
    setLaunchDate(toLocalInput(data.launchDate));
    setProgressPercent(Math.max(0, Math.min(100, Number(data.progressPercent) || 0)));
    setTitle(data.title || '');
    setMessage(data.message || '');
  }

  useEffect(() => {
    let alive = true;
    fetch(`${apiBase}/launch-config`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data: LaunchConfig) => {
        if (!alive) return;
        applyConfig(data);
        setStatus('idle');
      })
      .catch(() => {
        if (alive) setStatus('idle');
      });
    return () => {
      alive = false;
    };
  }, [apiBase]);

  async function save() {
    setStatus('saving');
    setErrorMsg('');
    try {
      // Auth is carried by the HttpOnly session cookie (credentials: 'include'),
      // never a bearer token read from localStorage (identity ADR: no token in
      // localStorage, no raw token threaded through props).
      const res = await fetch(`${apiBase}/launch-config`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled,
          launchDate: toISO(launchDate),
          progressPercent: Number(progressPercent),
          title,
          message,
        }),
      });
      if (!res.ok) {
        let detail = `Request failed (${res.status})`;
        try {
          const body = await res.json();
          if (body && body.message) detail = String(body.message);
        } catch {
          /* ignore */
        }
        if (res.status === 401 || /token/i.test(detail)) {
          detail = t('admin.launch.errors.sessionExpired', { defaultValue: 'Session expired \u2014 log out and back in, then save again.' });
        }
        throw new Error(detail);
      }
      const data: LaunchConfig = await res.json();
      applyConfig(data);
      setStatus('saved');
      window.setTimeout(() => setStatus((s) => (s === 'saved' ? 'idle' : s)), 3000);
    } catch (e: unknown) {
      setErrorMsg(e && (e as unknown as Record<string, unknown>).message ? String((e as unknown as Record<string, unknown>).message) : t('admin.launch.errors.saveFailed', { defaultValue: 'Could not save. Check the connection and try again.' }));
      setStatus('error');
    }
  }

  const busy = status === 'saving' || status === 'loading';

  return (
    <div className="kxla-wrap">
      <style>{CSS}</style>

      <div className="kxla-head">
        <div>
          <h3 className="kxla-title">{t('admin.launch.title', { defaultValue: 'Launch countdown' })}</h3>
          <p className="kxla-sub">{t('admin.launch.subtitle', { defaultValue: 'Controls the countdown modal shown on the homepage.' })}</p>
        </div>
        <label className="kxla-switch" title={enabled ? t('admin.launch.toggle.shown', { defaultValue: 'Shown to visitors' }) : t('admin.launch.toggle.hidden', { defaultValue: 'Hidden' })}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="kxla-slider" />
        </label>
      </div>

      <div className="kxla-grid">
        <div className="kxla-field">
          <label className="kxla-label" htmlFor="kxla-date">
            {t('admin.launch.fields.dateLabel', { defaultValue: 'Launch date & time' })}
          </label>
          <input
            id="kxla-date"
            type="datetime-local"
            className="kxla-input"
            value={launchDate}
            onChange={(e) => setLaunchDate(e.target.value)}
          />
          <div className="kxla-presets">
            <button type="button" className="kxla-preset" onClick={() => setLaunchDate(fromNowDays(7))}>
              {t('admin.launch.presets.in7days', { defaultValue: 'In 7 days' })}
            </button>
            <button type="button" className="kxla-preset" onClick={() => setLaunchDate(fromNowDays(30))}>
              {t('admin.launch.presets.in30days', { defaultValue: 'In 30 days' })}
            </button>
            <button type="button" className="kxla-preset" onClick={() => setLaunchDate(fromNowDays(60))}>
              {t('admin.launch.presets.in60days', { defaultValue: 'In 60 days' })}
            </button>
            <button type="button" className="kxla-preset kxla-preset-ghost" onClick={() => setLaunchDate('')}>
              {t('admin.launch.presets.clear', { defaultValue: 'Clear' })}
            </button>
          </div>
          <span className="kxla-hint">{t('admin.launch.fields.dateHint', { defaultValue: 'Set in your local time; visitors see the countdown in theirs.' })}</span>
        </div>

        <div className="kxla-field">
          <label className="kxla-label" htmlFor="kxla-pct">
            {t('admin.launch.fields.progressLabel', { defaultValue: 'Progress' })} — {progressPercent}%
          </label>
          <input
            id="kxla-pct"
            type="range"
            min={0}
            max={100}
            value={progressPercent}
            onChange={(e) => setProgressPercent(Number(e.target.value))}
            className="kxla-range"
          />
        </div>

        <div className="kxla-field">
          <label className="kxla-label" htmlFor="kxla-heading">
            {t('admin.launch.fields.titleLabel', { defaultValue: 'Title' })}
          </label>
          <input
            id="kxla-heading"
            type="text"
            className="kxla-input"
            value={title}
            maxLength={80}
            placeholder={t('admin.launch.fields.titlePlaceholder', { defaultValue: 'Launching soon' })}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="kxla-field">
          <label className="kxla-label" htmlFor="kxla-msg">
            {t('admin.launch.fields.messageLabel', { defaultValue: 'Message' })}
          </label>
          <textarea
            id="kxla-msg"
            className="kxla-input kxla-textarea"
            value={message}
            maxLength={240}
            rows={3}
            placeholder={t('admin.launch.fields.messagePlaceholder', { defaultValue: 'A short line about what is coming.' })}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
      </div>

      <div className="kxla-footer">
        <button className="kxla-save" onClick={save} disabled={busy}>
          {status === 'saving' ? t('admin.launch.actions.saving', { defaultValue: 'Saving\u2026' }) : t('admin.launch.actions.save', { defaultValue: 'Save changes' })}
        </button>
        {status === 'saved' ? <span className="kxla-ok">{t('admin.launch.msg.saved', { defaultValue: 'Saved \u2014 refresh the homepage to see it' })}</span> : null}
        {status === 'error' ? <span className="kxla-err">{errorMsg}</span> : null}
      </div>
    </div>
  );
}

const CSS = `
.kxla-wrap{--kxa:${ACCENT};max-width:560px;font-family:inherit;color:#12233b;background:#fff;border:1px solid #e7ecf2;border-radius:16px;padding:22px 22px 20px;}
.kxla-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:18px;}
.kxla-title{margin:0;font-size:17px;font-weight:700;}
.kxla-sub{margin:4px 0 0;font-size:13px;color:#6b7a8d;}
.kxla-switch{position:relative;display:inline-block;width:46px;height:26px;flex:none;cursor:pointer;}
.kxla-switch input{opacity:0;width:0;height:0;}
.kxla-slider{position:absolute;inset:0;background:#cdd6e0;border-radius:26px;transition:background .18s;}
.kxla-slider::before{content:"";position:absolute;height:20px;width:20px;left:3px;top:3px;background:#fff;border-radius:50%;transition:transform .18s;box-shadow:0 1px 3px rgba(0,0,0,.2);}
.kxla-switch input:checked + .kxla-slider{background:var(--kxa);}
.kxla-switch input:checked + .kxla-slider::before{transform:translateX(20px);}
.kxla-switch input:focus-visible + .kxla-slider{outline:2px solid var(--kxa);outline-offset:2px;}
.kxla-grid{display:flex;flex-direction:column;gap:16px;}
.kxla-field{display:flex;flex-direction:column;}
.kxla-label{font-size:13px;font-weight:600;margin-bottom:7px;color:#33465c;}
.kxla-input{font:inherit;font-size:14px;color:#12233b;padding:10px 12px;border:1px solid #d6dee7;border-radius:10px;background:#fff;transition:border-color .15s,box-shadow .15s;}
.kxla-input:focus{outline:none;border-color:var(--kxa);box-shadow:0 0 0 3px rgba(29,110,183,.15);}
.kxla-textarea{resize:vertical;min-height:64px;line-height:1.5;}
.kxla-presets{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px;}
.kxla-preset{font:inherit;font-size:12px;font-weight:600;color:var(--kxa);background:#eef4fb;border:1px solid #d6e4f3;padding:6px 12px;border-radius:999px;cursor:pointer;transition:background .15s;}
.kxla-preset:hover{background:#e2edf9;}
.kxla-preset:focus-visible{outline:2px solid var(--kxa);outline-offset:2px;}
.kxla-preset-ghost{color:#6b7a8d;background:#f4f6f9;border-color:#e2e7ee;}
.kxla-hint{margin-top:8px;font-size:12px;color:#8a97a8;}
.kxla-range{width:100%;accent-color:var(--kxa);}
.kxla-footer{display:flex;align-items:center;gap:14px;margin-top:22px;flex-wrap:wrap;}
.kxla-save{font:inherit;font-weight:600;font-size:14px;color:#fff;background:var(--kxa);border:none;padding:11px 20px;border-radius:10px;cursor:pointer;transition:filter .15s,opacity .15s;}
.kxla-save:hover{filter:brightness(1.06);}
.kxla-save:disabled{opacity:.6;cursor:default;}
.kxla-save:focus-visible{outline:2px solid var(--kxa);outline-offset:2px;}
.kxla-ok{font-size:13px;font-weight:600;color:#1a8a4f;}
.kxla-err{font-size:13px;color:#c0392b;}
`;
